import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import * as CM from '@radix-ui/react-context-menu';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  Bookmark,
  BookmarkCheck,
  CircleCheck,
  CircleDashed,
  Heart,
  House,
  Info,
  Library,
  ListChecks,
  ListPlus,
  Users,
  MessageCircle,
  Pencil,
  Pin,
  PinOff,
  Play,
  RefreshCw,
  Search,
  Settings,
} from 'lucide-react';
import type { MediaSummary } from '@teaplus/shared';
import { api, errorMessage } from '@/lib/api';
import { useInvalidateLibrary, useWatchedIds } from '@/lib/queries';
import { useFavouriteToggle } from '@/lib/use-favourites';
import { useWatchlistToggle } from '@/lib/use-watchlist';
import { useWindowControls } from '@/lib/desktop';
import { useActiveParty } from '@/lib/party';
import { chatPath, useCanChat } from '@/lib/social';

const content =
  'z-[80] min-w-56 overflow-hidden rounded-2xl border border-white/10 bg-bg-2/95 p-1.5 shadow-2xl backdrop-blur-xl animate-[fade-in_120ms_ease-out]';
const item =
  'flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2 text-[14px] text-text/90 outline-none data-[highlighted]:bg-white/8 data-[highlighted]:text-text data-[disabled]:pointer-events-none data-[disabled]:opacity-40 [&_svg]:h-4 [&_svg]:w-4 [&_svg]:text-muted data-[highlighted]:[&_svg]:text-pink';

function Item({
  icon,
  children,
  onSelect,
  disabled,
}: {
  icon: ReactNode;
  children: ReactNode;
  onSelect: () => void;
  disabled?: boolean;
}) {
  return (
    <CM.Item className={item} onSelect={onSelect} disabled={disabled}>
      {icon}
      {children}
    </CM.Item>
  );
}
const Separator = () => <CM.Separator className="my-1 h-px bg-line" />;

/** In a party, any card can go straight into the queue (a series from its first episode). */
function QueueItem({ media }: { media: MediaSummary }) {
  const { conn } = useActiveParty();
  if (!conn) return null;
  const series = media.type === 'series';
  return (
    <Item
      icon={<ListPlus />}
      onSelect={() =>
        void conn
          .nominate({
            type: media.type,
            id: media.id,
            season: series ? 1 : null,
            episode: series ? 1 : null,
          })
          .then(() => toast.success('Added to the queue'))
          .catch((e) => toast.error(errorMessage(e)))
      }
    >
      Add to queue
    </Item>
  );
}

export function MediaContextMenu({
  media,
  children,
}: {
  media: MediaSummary;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const wl = useWatchlistToggle();
  const fav = useFavouriteToggle();
  const watched = useWatchedIds().data?.has(media.id) ?? false;
  const invalidate = useInvalidateLibrary();
  const details = media.type === 'movie' ? `/movie/${media.id}` : `/series/${media.id}`;

  async function toggleWatched() {
    try {
      await api('/api/history/watched', {
        body: {
          mediaId: media.id,
          contentType: media.type,
          watched: !watched,
          snapshot: {
            title: media.title,
            posterUrl: media.posterUrl,
            backdropUrl: media.backdropUrl,
            year: media.year,
          },
        },
      });
      invalidate();
      toast.success(
        watched ? `Marked ${media.title} as unwatched` : `Marked ${media.title} as watched`,
      );
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }

  return (
    <CM.Root>
      <CM.Trigger asChild>{children}</CM.Trigger>
      <CM.Portal>
        <CM.Content className={content} aria-label={`${media.title} actions`}>
          <CM.Label className="truncate px-3 pb-1.5 pt-1 text-xs font-semibold text-muted">
            {media.title}
          </CM.Label>
          <Item icon={<Play />} onSelect={() => navigate(details)}>
            {media.type === 'movie' ? 'Play' : 'Choose an episode'}
          </Item>
          <Item icon={<Info />} onSelect={() => navigate(details)}>
            View details
          </Item>
          <QueueItem media={media} />
          <Separator />
          <Item
            icon={wl.has(media.id) ? <BookmarkCheck /> : <Bookmark />}
            onSelect={() => wl.toggle(media)}
          >
            {wl.has(media.id) ? 'Remove from Watch Later' : 'Add to Watch Later'}
          </Item>
          <Item
            icon={<Heart className={fav.has(media.id) ? 'fill-current' : undefined} />}
            onSelect={() => fav.toggle(media)}
          >
            {fav.has(media.id) ? 'Remove from Favourites' : 'Add to Favourites'}
          </Item>
          <Item
            icon={watched ? <CircleDashed /> : <CircleCheck />}
            onSelect={() => void toggleWatched()}
          >
            {watched ? 'Mark as unwatched' : 'Mark as watched'}
          </Item>
        </CM.Content>
      </CM.Portal>
    </CM.Root>
  );
}

/**
 * Right-click on an episode: play it, start a Watch Together for it, or mark it (or the season so
 * far) as watched.
 */
export function EpisodeContextMenu({
  label,
  watched,
  aired,
  onPlay,
  onStartParty,
  partyLabel = 'Start Watch Together',
  onToggleWatched,
  onMarkUpTo,
  onMarkSeason,
  children,
}: {
  label: string;
  watched: boolean;
  aired: boolean;
  onPlay: () => void;
  onStartParty?: () => void;
  partyLabel?: string;
  onToggleWatched: () => void;
  onMarkUpTo?: () => void;
  onMarkSeason?: () => void;
  children: ReactNode;
}) {
  return (
    <CM.Root>
      <CM.Trigger asChild>{children}</CM.Trigger>
      <CM.Portal>
        <CM.Content className={content} aria-label={`${label} actions`}>
          <CM.Label className="truncate px-3 pb-1.5 pt-1 text-xs font-semibold text-muted">
            {label}
          </CM.Label>
          <Item icon={<Play />} onSelect={onPlay} disabled={!aired}>
            Play episode
          </Item>
          {onStartParty && (
            <Item icon={<Users />} onSelect={onStartParty} disabled={!aired}>
              {partyLabel}
            </Item>
          )}
          <Separator />
          <Item icon={watched ? <CircleDashed /> : <CircleCheck />} onSelect={onToggleWatched}>
            {watched ? 'Mark as unwatched' : 'Mark as watched'}
          </Item>
          {onMarkUpTo && (
            <Item icon={<ListChecks />} onSelect={onMarkUpTo}>
              Mark this and earlier episodes as watched
            </Item>
          )}
          {onMarkSeason && (
            <Item icon={<ListChecks />} onSelect={onMarkSeason}>
              Mark the whole season as watched
            </Item>
          )}
        </CM.Content>
      </CM.Portal>
    </CM.Root>
  );
}

/** Right-click on a friend (their card, or something they did): join, view, nickname, manage. */
export function FriendContextMenu({
  friend,
  watching,
  onRequestJoin,
  onEditLook,
  children,
}: {
  friend: { id: string; name: string };
  /** What they're doing that this card is about, if anything. */
  watching?: { title: string; path: string; live: boolean } | null;
  onRequestJoin?: () => void;
  onEditLook?: () => void;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const canChat = useCanChat();
  return (
    <CM.Root>
      <CM.Trigger asChild>{children}</CM.Trigger>
      <CM.Portal>
        <CM.Content className={content} aria-label={`${friend.name} actions`}>
          <CM.Label className="truncate px-3 pb-1.5 pt-1 text-xs font-semibold text-muted">
            {friend.name}
          </CM.Label>
          {watching?.live && onRequestJoin && (
            <Item icon={<Users />} onSelect={onRequestJoin}>
              Request to Join
            </Item>
          )}
          {watching && (
            <Item icon={<Info />} onSelect={() => navigate(watching.path)}>
              View {watching.title}
            </Item>
          )}
          {watching && <Separator />}
          {canChat && (
            <Item icon={<MessageCircle />} onSelect={() => navigate(chatPath(friend.id))}>
              Message
            </Item>
          )}
          {onEditLook && (
            <Item icon={<Pencil />} onSelect={onEditLook}>
              Nickname and picture…
            </Item>
          )}
          <Item icon={<Users />} onSelect={() => navigate('/friends?tab=manage')}>
            Manage friends
          </Item>
        </CM.Content>
      </CM.Portal>
    </CM.Root>
  );
}

const isTextTarget = (t: EventTarget | null) => {
  const el = t as HTMLElement | null;
  return (
    !!el?.closest?.('input, textarea, [contenteditable="true"], select') ||
    !!window.getSelection()?.toString()
  );
};

export function AppContextMenu({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const win = useWindowControls();
  const [disabled, setDisabled] = useState(false);
  return (
    <CM.Root>
      <CM.Trigger
        asChild
        disabled={disabled}
        onPointerDownCapture={(e) => e.button === 2 && setDisabled(isTextTarget(e.target))}
      >
        <div>{children}</div>
      </CM.Trigger>
      <CM.Portal>
        <CM.Content className={content} aria-label="Tea+ menu">
          <Item icon={<ArrowLeft />} onSelect={() => navigate(-1)}>
            Back
          </Item>
          <Item icon={<ArrowRight />} onSelect={() => navigate(1)}>
            Forward
          </Item>
          <Item icon={<RefreshCw />} onSelect={() => window.location.reload()}>
            Reload
          </Item>
          <Separator />
          <Item icon={<House />} onSelect={() => navigate('/home')}>
            Home
          </Item>
          <Item icon={<Search />} onSelect={() => navigate('/search')}>
            Search
          </Item>
          <Item icon={<Library />} onSelect={() => navigate('/library')}>
            My Library
          </Item>
          <Item icon={<Settings />} onSelect={() => navigate('/settings')}>
            Settings
          </Item>
          {win && (
            <>
              <Separator />
              <Item
                icon={win.pinned ? <PinOff /> : <Pin />}
                onSelect={() => void win.setPinned(!win.pinned)}
              >
                {win.pinned ? 'Stop keeping on top' : 'Keep on top of other windows'}
              </Item>
            </>
          )}
        </CM.Content>
      </CM.Portal>
    </CM.Root>
  );
}
